import React, {
  useEffect,
  useMemo,
  useRef,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import {
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import {
  useNotify,
} from 'core/hooks';
import Button from 'pages/Dashboard/components/Button';
import Input from 'pages/Dashboard/components/Input';
import Select from 'core/components/Select';
import {
  useGetSectionTemplateByName,
  useSaveSectionTemplate,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/hooks';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  isEmptyString,
  notEmptyString,
} from '@populateui/utils';
import {
  getSectionLabelByName,
  normalizeContent,
  serializeContent,
} from 'views/EMR/Settings/helper';
import Spinner from 'pages/Dashboard/components/Spinner';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import TextArea from 'core/components/TextArea';
import {
  visitNoteFormTemplateAction,
} from 'views/EMR/VisitNoteForm';
import ReadonlyNote from 'views/EMR/Settings/VisitNotes/ReadonlyNote';

type SectionOption = {
  value: string;
  label: string;
};

type SectionTemplateForm = {
  section: SectionOption;
  templateName: string;
  content?: string;
};

const sectionNames = [
  'AdditionalHPITemplate',
  'PhysicalExamTemplate',
  'PlanTemplate',
  'RadiologyTemplate',
];

const options = sectionNames.map((name) => ({
  value: name,
  label: getSectionLabelByName(name),
}));

export default function SectionTemplate({ entity, onClose }: SidePanelComponentProps) {
  const userInfo = useUserInfo();
  const { templateName, sectionName, userId } = entity ?? {};
  const templateNameInputRef = useRef<HTMLInputElement>(null);
  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid, isSubmitting },
    watch,
  } = useForm<SectionTemplateForm>({ mode: 'all' });
  const isNew = isEmptyString(templateName);
  const { data: content, isFetching } = useGetSectionTemplateByName(templateName, sectionName);
  const notify = useNotify();
  const { mutateAsync: saveSectionTemplate } = useSaveSectionTemplate();

  const selectedSection = watch('section');

  const selectedSectionName = useMemo(
    () => (isEmptyString(sectionName) ? selectedSection?.value : sectionName),
    [selectedSection, sectionName],
  );

  useEffect(() => {
    if (notEmptyString(content)) {
      reset({
        templateName,
        section: { value: sectionName },
        content: normalizeContent(content?.toString() ?? ''),
      });
    }
  }, [templateName, sectionName, content]);
  const onSubmit = async (data: SectionTemplateForm) => {
    const {
      templateName,
      section: { value: sectionName },
      content,
    } = data;
    try {
      const template = await saveSectionTemplate({
        templateName,
        sectionName,
        content: serializeContent(content),
        isSavingANewTemplate: isNew,
      });
      notify({
        message: 'Template saved successfully',
        variant: 'success',
      });
      settingsEventChannel.emit('onVisitNoteSettingsChange', 'section-templates', {
        ...entity,
        templateName,
        sectionName,
        userId: template?.userId,
      });
    } finally {
      reset(data, { keepIsSubmitted: false });
    }
  };

  const readonly = userInfo?.user?.userId !== userId && !isNew;

  useEffect(() => {
    templateNameInputRef?.current?.focus?.();
  }, []);

  return isFetching
    ? (
      <div className="w-full flex h-full items-center justify-center">
        <Spinner size={32} />
      </div>
    ) : (
      <div className="w-[40rem] h-full flex flex-col">
        <div className="flex w-full gap-2 justify-between !items-start border-b border-b-gray-300 p-4 populate-form">
          <Controller
            name="templateName"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState: { invalid } }) => (
              isNew
                ? (
                  <Input
                    {...field}
                    error={invalid}
                    label="Template Name"
                    className="flex-1"
                    value={field.value ?? ''}
                    id="templateName"
                    placeholder="Enter template name"
                    inputRef={templateNameInputRef}
                    allowSpeechToText
                    required
                  />
                )
                : <h3 className="font-semibold">{field?.value?.toString() ?? '--'}</h3>
            )}
          />
          <div className="flex h-10 relative -top-4 -right-4">
            <IconButton onClick={onClose}>
              <CloseIcon className="!w-5 !h-5 !text-inherit" />
            </IconButton>
          </div>
        </div>
        <div className="flex-1 w-full flex flex-col gap-form-row overflow-auto px-4 pt-4 populate-form">
          {isNew && (
            <Controller
              control={control}
              name="section"
              rules={{ required: true }}
              render={({ field, fieldState: { invalid } }) => (
                <Select<SectionOption>
                  id="section-name"
                  options={options}
                  {...field}
                  placeholder="Select Template Section"
                  error={invalid}
                  label="Section"
                  getValue={({ value }) => value}
                  getLabel={({ label }) => label}
                  required
                />
              )}
            />
          )}
          <Controller
            control={control}
            name="content"
            rules={{ required: true }}
            render={({ field }) => (
              <TextArea
                {...field}
                value={field?.value?.toString() ?? ''}
                actions={['dictation', visitNoteFormTemplateAction, 'field']}
                disabled={readonly}
                id={selectedSectionName}
              />
            )}
          />
        </div>
        <div className="flex justify-between items-center w-full border-t border-t-gray-300 p-4">
          <div className="flex gap-4">
            {!readonly && (
              <Button
                color="primary"
                variant="contained"
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit(onSubmit)}
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
          <ReadonlyNote readonly={readonly} />
        </div>
      </div>
    );
}
